import React, { useState } from 'react';
import './MockPaymentOverlay.css';
import TermsOverlay from '../terms/TermsOverlay';

interface MockPaymentOverlayProps {
    selectedPlan: string;
    billingCycle: 'monthly' | 'yearly';
    planPrice: number;
    vehicleCount: number;
    onClose: () => void;
    onSuccess: () => void;
}

export default function MockPaymentOverlay({ 
    selectedPlan, 
    billingCycle, 
    planPrice, 
    vehicleCount,
    onClose,
    onSuccess 
}: MockPaymentOverlayProps) {
    const [step, setStep] = useState<'details' | 'processing' | 'success'>('details');
    const [agreed, setAgreed] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState<'card' | 'transfer' | 'virtual'>('card');
    const [termsModal, setTermsModal] = useState<'service' | 'location' | 'privacy' | null>(null);

    const handlePayment = () => {
        if (!agreed) {
            alert('이용 약관에 동의해주세요.');
            return;
        }
        setStep('processing');
        // Simulate API call and processing time
        setTimeout(() => {
            setStep('success');
            setTimeout(() => {
                onSuccess();
            }, 2000);
        }, 1500);
    };

    const monthlyTotal = planPrice * vehicleCount;
    const finalAmount = billingCycle === 'yearly' ? monthlyTotal * 12 : monthlyTotal;

    return (
        <div className="payment-overlay">
            <div className="payment-container" style={{ maxWidth: step === 'details' ? '540px' : '450px' }}>
                {step === 'details' && (
                    <>
                        <div className="payment-header">
                            <h3 className="payment-title">고객님의 주문</h3>
                            <button className="close-btn-small" onClick={onClose}>&times;</button>
                        </div>
                        <div className="payment-content">
                            {/* Order Summary Table */}
                            <div className="order-summary-table">
                                <div className="order-table-header">
                                    <span className="col-product">상품</span>
                                    <span className="col-subtotal">소계</span>
                                </div>
                                <div className="order-table-row">
                                    <div className="col-product">
                                        <span className="product-icon">🔥</span>
                                        <span className="product-name">EView {selectedPlan} 플랜 ({billingCycle === 'monthly' ? '월' : '연'}간 결제) × {vehicleCount}대</span>
                                    </div>
                                    <div className="col-subtotal">₩{finalAmount.toLocaleString()}</div>
                                </div>
                                <div className="order-table-row subtotal-row">
                                    <span className="col-label">소계</span>
                                    <span className="col-value">₩{finalAmount.toLocaleString()}</span>
                                </div>
                                <div className="order-table-row total-row">
                                    <span className="col-label">총계</span>
                                    <span className="col-value">₩{finalAmount.toLocaleString()}</span>
                                </div>
                            </div>

                            {/* Payment Methods */}
                            <div className="payment-method-section">
                                <div className="method-option-list">
                                    <label className={`method-radio-item ${paymentMethod === 'card' ? 'active' : ''}`}>
                                        <input 
                                            type="radio" 
                                            name="paymentMethod" 
                                            checked={paymentMethod === 'card'} 
                                            onChange={() => setPaymentMethod('card')} 
                                        />
                                        <span className="radio-circle"></span>
                                        <span className="method-text">신용카드</span>
                                    </label>
                                    <label className={`method-radio-item ${paymentMethod === 'transfer' ? 'active' : ''}`}>
                                        <input 
                                            type="radio" 
                                            name="paymentMethod" 
                                            checked={paymentMethod === 'transfer'} 
                                            onChange={() => setPaymentMethod('transfer')} 
                                        />
                                        <span className="radio-circle"></span>
                                        <span className="method-text">실시간 계좌이체</span>
                                    </label>
                                    <label className={`method-radio-item ${paymentMethod === 'virtual' ? 'active' : ''}`}>
                                        <input 
                                            type="radio" 
                                            name="paymentMethod" 
                                            checked={paymentMethod === 'virtual'} 
                                            onChange={() => setPaymentMethod('virtual')} 
                                        />
                                        <span className="radio-circle"></span>
                                        <span className="method-text">가상계좌</span>
                                    </label>
                                </div>
                                
                                <div className="privacy-notice">
                                    개인 데이터는 주문을 처리하고, 이 웹사이트 전체에서 사용자 환경을 지원하며, <span className="link-text" onClick={() => setTermsModal('privacy')}>개인정보 보호정책</span>에 설명한 기타 목적을 이행하는 데 사용됩니다.
                                </div>

                                <div className="terms-agreement-check">
                                    <label className="agreement-label">
                                        <input 
                                            type="checkbox" 
                                            checked={agreed} 
                                            onChange={(e) => setAgreed(e.target.checked)} 
                                        />
                                        <span className="checkbox-custom"></span>
                                        <span className="agreement-text">
                                            본인은 웹사이트 <span className="link-text" onClick={() => setTermsModal('service')}>이용 약관</span>을 읽었으며 이에 동의합니다. <span className="required">*</span>
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="payment-footer">
                            <button className="confirm-checkout-btn" onClick={handlePayment}>
                                주문 확정
                            </button>
                        </div>
                    </>
                )}

                {step === 'processing' && (
                    <div className="payment-processing">
                        <div className="payment-spinner"></div>
                        <p>결제를 진행중입니다...</p>
                        <span className="sub-text">창을 닫거나 새로고침하지 마세요.</span>
                    </div>
                )}

                {step === 'success' && (
                    <div className="payment-success">
                        <div className="success-icon-wrap">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                            </svg>
                        </div>
                        <h3 className="success-title">결제가 완료되었습니다!</h3>
                        <p className="success-desc">EView {selectedPlan} 서비스를 이용해보세요.</p>
                        <p className="redirect-hint">대시보드로 이동합니다...</p>
                    </div>
                )}
            </div>
            {termsModal && (
                <TermsOverlay type={termsModal} onClose={() => setTermsModal(null)} />
            )}
        </div>
    );
}
